import { Box, Pagination, Typography } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router';
import { Business_Profile_Enum, Product_Sub_Family_Name_Enum, User_Role_Enum } from '@kheops/graphql';
import { PageWrapper } from '../common/components/PageWrapper';
import { preferredProductFamilyAtom } from '../common/state/state';
import { ContractPreview } from '../hooks/useContracts';
import ProductFilterToolbar from '../common/components/ProductFilterToolbar';
import EmptyPagePlaceholder from '../common/components/EmptyPagePlaceholder';
import { RoutePaths } from '../routes/AppRoutes';
import GuitaristButterflyImage from '../assets/images/guitarist_butterfly.png';
import { currentContextAtom, signedContractsAtom } from '../state';
import ContractRow from './ContractRow';

interface ContractListHistoryState {
  page?: number;
  query?: string;
}

export default function ContractList(): React.JSX.Element {
  const PAGE_SIZE = 20;

  const { t } = useTranslation(['common', 'contracts']);
  const { realm, userAdministrativeAreas, userBrands, roleName } = useAtomValue(currentContextAtom);
  const contracts = useAtomValue(signedContractsAtom);
  const [preferredProductFamilies, setPreferredProductFamilies] = useAtom(preferredProductFamilyAtom);
  const [filteredContracts, setFilteredContracts] = useState<ContractPreview[]>([]);
  const [contractRows, setContractRows] = useState<ContractPreview[]>([]);
  const [page, setPage] = useState((window.history.state.uiState as ContractListHistoryState)?.page || 1);
  const brands = userBrands?.map(({ brand }) => brand);
  const areas = userAdministrativeAreas?.map(({ area }) => area);

  const shouldDisplayMyClientsFilter = realm === Business_Profile_Enum.Supplier
    && [User_Role_Enum.SupplierSalesAdmin, User_Role_Enum.SupplierSalesRep].includes(roleName)
    && (
      (!!userBrands?.length && !userBrands[0]?.has_all_brands)
      || (!!userAdministrativeAreas?.length && !userAdministrativeAreas[0].has_all_areas)
    );

  const updateHistoryState = (newState: ContractListHistoryState): void => {
    window.history.replaceState({
      ...window.history.state,
      uiState: {
        ...window.history.state.uiState,
        ...newState,
      },
    }, '');
  };

  const updatePage = (newPage: number): void => {
    window.scrollTo(0, 0);
    setPage(newPage);
    updateHistoryState({ page: newPage });
  };

  const handleSearch = useCallback((query: string, productFamilies: Product_Sub_Family_Name_Enum[], isMyClientsFilterActivated: boolean, skipPaginationReset?: boolean): void => {
    const shouldMatchBrands = isMyClientsFilterActivated && userBrands?.length && !userBrands[0]?.has_all_brands;
    const shouldMatchAreas = isMyClientsFilterActivated && userAdministrativeAreas?.length && !userAdministrativeAreas[0]?.has_all_areas;

    const result = contracts.filter((contract) => {
      const matchQuery = contract.company.tradeName.toLowerCase().includes(query.toLowerCase());
      const matchProductFamilies = !productFamilies.length
        || contract.company.products?.some((product) => productFamilies.includes(product.sub_family as Product_Sub_Family_Name_Enum));
      const matchMyBrands = !shouldMatchBrands || brands!.includes(contract.company.brand);
      const matchMyAreas = !shouldMatchAreas || areas!.includes(contract.company.addresses[0].administrativeAreaLevel2);

      return matchQuery && matchProductFamilies && matchMyBrands && matchMyAreas;
    });

    if (!skipPaginationReset) {
      updatePage(1);
    }

    setPreferredProductFamilies(productFamilies);
    setFilteredContracts(result);
    updateHistoryState({ query });
  }, []);

  const onlyMyClientsCount = useMemo(() => {
    const shouldMatchBrands = userBrands?.length && !userBrands[0]?.has_all_brands;
    const shouldMatchAreas = userAdministrativeAreas?.length && !userAdministrativeAreas[0]?.has_all_areas;
    const query: string = window.history.state.uiState?.query || '';

    return contracts.filter((contract) => {
      const matchMyBrands = !shouldMatchBrands || brands!.includes(contract.company.brand);
      const matchMyAreas = !shouldMatchAreas || areas!.includes(contract.company.addresses[0].administrativeAreaLevel2);
      const matchQuery = contract.company.tradeName.toLocaleLowerCase().includes(query.toLocaleLowerCase());

      return matchMyBrands && matchMyAreas && matchQuery;
    }).length;
  }, [contracts, window.history.state]);

  const emptyPagePlaceholderProps = realm === Business_Profile_Enum.Buyer
    ? {
      title: t('contracts:no_contractualized_supplier'),
      description: t('contracts:no_contractualized_supplier_description'),
      imageSrc: GuitaristButterflyImage,
      buttonLabel: t('contracts:discover_new_suppliers'),
      linkDestination: generatePath(RoutePaths.DISCOVERY_SUPPLIERS),
    }
    : {
      title: t('contracts:no_contractualized_buyer'),
      description: t('contracts:no_contractualized_buyer_description'),
      imageSrc: GuitaristButterflyImage,
      buttonLabel: t('contracts:discover_new_buyers'),
      linkDestination: generatePath(RoutePaths.DISCOVERY_BUYERS),
    };

  useEffect(() => {
    const start = PAGE_SIZE * (page - 1);

    setContractRows(filteredContracts.slice(start, start + PAGE_SIZE));
  }, [page, filteredContracts]);

  useEffect(() => {
    handleSearch(
      (window.history.state.uiState as ContractListHistoryState)?.query || '',
      preferredProductFamilies,
      shouldDisplayMyClientsFilter && !!onlyMyClientsCount,
      true,
    );
  }, []);

  const products = useMemo(() => {
    return contracts.flatMap((contract) => {
      if (!contract.company.products) {
        return [];
      }

      return contract.company.products.map((product) => product);
    });
  }, [contracts]);

  return (
    <PageWrapper>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant="headlineMedium">
          {t(`contracts:${realm === Business_Profile_Enum.Buyer ? 'supplier_on_contract' : 'customer_on_contract'}`, { count: contracts.length })}
        </Typography>
        <ProductFilterToolbar
          shouldDisplayClearAllButton={realm === Business_Profile_Enum.Buyer}
          products={products}
          onChange={handleSearch}
          initialQuery={(window.history.state.uiState as ContractListHistoryState)?.query}
          initialProductFamilies={preferredProductFamilies}
          filteredItemsNumber={onlyMyClientsCount}
        />
        {
          !contracts.length
            ? (
              <EmptyPagePlaceholder {...emptyPagePlaceholderProps} />
            ) : (
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <>
                {
                  filteredContracts.length
                    ? (
                      <>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {contractRows.map((contract) => <ContractRow key={contract.id} contract={contract} />)}
                        </Box>
                        <Pagination
                          color="primary"
                          sx={{
                            '& .MuiPagination-ul': {
                              justifyContent: 'center',
                            },
                          }}
                          page={page}
                          count={Math.ceil(filteredContracts.length / PAGE_SIZE)}
                          onChange={(event, newPage) => updatePage(newPage)}
                        />
                      </>
                    )
                    : (
                      <Box sx={{ textAlign: 'center', pt: 5 }}>
                        <Typography variant="titleMedium">
                          {t(`contracts:${realm === Business_Profile_Enum.Buyer ? 'no_supplier_matches_your_search' : 'no_customer_matches_your_search'}`)}
                        </Typography>
                      </Box>
                    )
                }
              </>
            )
        }
      </Box>
    </PageWrapper>
  );
}
